<div class="case-report-builder-page dynamic-case-report-page">
  <blocking-loader-overlay
    :show="total_loading"
    :message="page_loading_message"
    :detail="page_loading_detail"
  />

  <main class="case-report-builder-panel dynamic-case-report-panel">
    <section class="company-cases-header dynamic-report-page-header">
      <div class="company-cases-hero dynamic-report-page-hero">
        <div class="dynamic-report-heading-row">
          <div>
            <div class="company-cases-breadcrumb text-uppercase d-flex align-items-center gap-2">
              <span>Reports</span>
              <span>/</span>
              <span>Report</span>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section
      class="dynamic-report-tab-panel"
    >
      <section class="case-report-builder-filters">
        <div class="d-flex justify-content-between align-items-start gap-3 flex-wrap mb-3">
          <div>
            <h4 class="mb-1">Report Filters</h4>
          </div>
          <div class="dynamic-report-actions">
            <button
              type="button"
              class="btn btn-light border secondary-color d-inline-flex align-items-center justify-content-center gap-2"
              :disabled="checking_dynamic_report"
              @click="check_dynamic_report"
            >
              <i class="bi bi-check2-circle"></i>
              <span>{{ checking_dynamic_report ? 'Checking...' : 'Check' }}</span>
            </button>
            <button
              type="button"
              class="btn btn-light border secondary-color d-inline-flex align-items-center justify-content-center gap-2"
              :disabled="previewing_dynamic_report"
              @click="preview_dynamic_report"
            >
              <i class="bi bi-table"></i>
              <span>{{ previewing_dynamic_report ? 'Loading...' : 'Preview' }}</span>
            </button>
            <button
              type="button"
              class="btn btn-primary-dark case-report-builder-action d-inline-flex align-items-center justify-content-center gap-2"
              :disabled="generating_dynamic_report"
              @click="generate_dynamic_report"
            >
              <i class="bi bi-download"></i>
              <span>{{ generating_dynamic_report ? 'Generating...' : 'Generate' }}</span>
            </button>
          </div>
        </div>

        <div class="case-report-builder-grid">
          <label class="case-report-builder-field">
            <span>Start Date</span>
            <input
              v-model="report_form.start_date"
              type="date"
              class="form-control case-report-builder-control"
            >
          </label>

          <label class="case-report-builder-field">
            <span>End Date</span>
            <input
              v-model="report_form.end_date"
              type="date"
              class="form-control case-report-builder-control"
            >
          </label>

          <label class="case-report-builder-field">
            <span>Domain</span>
            <select
              v-model="report_form.client_type"
              class="form-select case-report-builder-control"
            >
              <option v-if="!is_coordinator_scope_report" value="">All Domains</option>
              <option
                v-for="option in client_type_options"
                :key="`client-type-${option.id}`"
                :value="String(option.id)"
              >
                {{ option.name }}
              </option>
            </select>
          </label>

          <label class="case-report-builder-field">
            <span>Status</span>
            <select
              v-model="report_form.case_status"
              class="form-select case-report-builder-control"
            >
              <option value="">All Statuses</option>
              <option
                v-for="option in case_status_options"
                :key="`case-status-${option.id}`"
                :value="String(option.id)"
              >
                {{ option.name }}
              </option>
            </select>
          </label>

          <label class="case-report-builder-field">
            <span>Output Format</span>
            <select
              v-model="selected_format"
              class="form-select case-report-builder-control"
            >
              <option
                v-for="option in output_format_options"
                :key="`output-${option}`"
                :value="option"
              >
                {{ output_format_label(option) }}
              </option>
            </select>
          </label>
        </div>
      </section>

      <div
        v-if="reports_error"
        class="case-report-builder-alert"
      >
        {{ reports_error }}
      </div>

      <section class="dynamic-report-selected-fields">
        <div class="dynamic-report-section-head mb-3">
          <span class="fs-6"> Select Fields to Include in Excel</span>
          <strong>{{ selected_excel_field_count }}</strong>
        </div>

        <div
          v-if="selected_dynamic_fields.length"
          class="dynamic-report-selected-check-grid"
        >
          <label
            v-for="field in selected_dynamic_fields"
            :key="`download-field-${field.key}`"
            class="dynamic-report-selected-check"
            :class="{ 'is-selected': is_excel_field_selected(field.key) }"
          >
            <input
              type="checkbox"
              :checked="is_excel_field_selected(field.key)"
              @change="toggle_excel_field(field.key)"
            >
            <span>
              <strong>{{ field.label }}</strong>
            </span>
          </label>
        </div>

        <div
          v-else
          class="dynamic-report-empty"
        >
          No configured fields available.
        </div>
      </section>

      <section class="dynamic-report-preview">
        <div class="dynamic-report-section-head mb-3">
          <span class="fs-6">Preview</span>
          <strong>{{ preview_count }} row{{ preview_count === 1 ? '' : 's' }}</strong>
        </div>

        <div
          v-if="previewing_dynamic_report"
          class="dynamic-report-loading"
        >
          <span class="spinner-border spinner-border-sm"></span>
          <span>Loading preview...</span>
        </div>

        <div
          v-else-if="visible_preview_rows.length"
          class="table-responsive dynamic-report-table-wrap"
        >
          <table class="table table-sm table-hover dynamic-report-table">
            <thead>
              <tr>
                <th
                  v-for="column in report_columns"
                  :key="`preview-head-${column.key}`"
                >
                  {{ column.label }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(row, row_index) in visible_preview_rows"
                :key="`preview-row-${row_index}`"
              >
                <td
                  v-for="column in report_columns"
                  :key="`preview-cell-${row_index}-${column.key}`"
                >
                  <span :title="row_value(row, column.key)">{{ row_value(row, column.key) }}</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div
          v-else
          class="dynamic-report-empty"
        >
          No preview rows loaded.
        </div>
      </section>

      <section class="dynamic-report-history">
        <div class="dynamic-report-section-head mb-3">
          <span class="fs-6">Generated Reports</span>
        </div>

        <div
          v-if="loading_dynamic_history"
          class="dynamic-report-loading"
        >
          <span class="spinner-border spinner-border-sm"></span>
          <span>Loading generated reports...</span>
        </div>

        <div
          v-else-if="active_history_rows.length"
          class="dynamic-report-history-list"
        >
          <article
            v-for="report in paginated_history_rows"
            :key="`generated-report-${report.report_id || report.id}`"
            class="dynamic-report-history-row"
          >
            <div>
              <div class="dynamic-report-history-title-row">
                <strong>{{ generated_report_title(report) }}</strong>
                <span
                  class="dynamic-report-format-badge"
                  :class="generated_report_format_class(report)"
                >
                  {{ generated_report_format_label(report) }}
                </span>
              </div>
              <span>{{ generated_report_meta(report) }}</span>
            </div>
            <button
              type="button"
              class="btn btn-light border secondary-color d-inline-flex align-items-center justify-content-center gap-2"
              @click="open_saved_generated_report(report)"
            >
              <i class="bi bi-box-arrow-up-right"></i>
              <span>Open</span>
            </button>
          </article>

          <div class="dynamic-report-history-pagination">
            <span class="dynamic-report-pagination-summary">
              {{ history_page_start }}-{{ history_page_end }} of {{ active_history_count }}
            </span>
            <div
              v-if="history_page_count > 1"
              class="d-inline-flex align-items-center rounded-3 border overflow-hidden bg-white"
            >
              <button
                type="button"
                class="btn btn-sm btn-light border-0 rounded-0 px-2"
                aria-label="First page"
                :disabled="bounded_history_page === 1"
                @click="set_generated_history_page(1)"
              >
                <i class="bi bi-chevron-double-left"></i>
              </button>
              <button
                type="button"
                class="btn btn-sm btn-light border-0 border-start rounded-0 px-2"
                aria-label="Previous page"
                :disabled="bounded_history_page === 1"
                @click="set_generated_history_page(bounded_history_page - 1)"
              >
                <i class="bi bi-chevron-left"></i>
              </button>
              <button
                v-for="page in history_page_numbers"
                :key="`history-page-${page}`"
                type="button"
                class="btn btn-sm rounded-0 border-0 border-start px-3 pagination-seg-btn"
                :class="page === bounded_history_page ? 'btn-primary-dark text-white' : 'btn-light'"
                :aria-current="page === bounded_history_page ? 'page' : null"
                @click="set_generated_history_page(page)"
              >
                {{ page }}
              </button>
              <button
                type="button"
                class="btn btn-sm btn-light border-0 border-start rounded-0 px-2"
                aria-label="Next page"
                :disabled="bounded_history_page === history_page_count"
                @click="set_generated_history_page(bounded_history_page + 1)"
              >
                <i class="bi bi-chevron-right"></i>
              </button>
              <button
                type="button"
                class="btn btn-sm btn-light border-0 border-start rounded-0 px-2"
                aria-label="Last page"
                :disabled="bounded_history_page === history_page_count"
                @click="set_generated_history_page(history_page_count)"
              >
                <i class="bi bi-chevron-double-right"></i>
              </button>
            </div>
          </div>
        </div>

        <div
          v-else
          class="dynamic-report-empty"
        >
          No generated reports found.
        </div>
      </section>
    </section>
  </main>

  <div
    v-if="generated_report_preview_open"
    class="generated-report-preview-backdrop"
    role="dialog"
    aria-modal="true"
    @click.self="close_generated_report_preview"
  >
    <section class="generated-report-preview-modal">
      <header class="generated-report-preview-header">
        <div>
          <h2>{{ generated_report_preview_title }}</h2>
          <span>{{ generated_report_preview_type === 'pdf' ? 'PDF preview' : 'Excel preview' }}</span>
        </div>
        <button
          type="button"
          class="btn btn-light border generated-report-preview-close"
          aria-label="Close preview"
          @click="close_generated_report_preview"
        >
          <i class="bi bi-x-lg"></i>
        </button>
      </header>

      <div class="generated-report-preview-body">
        <div
          v-if="loading_generated_report_preview"
          class="dynamic-report-loading"
        >
          <span class="spinner-border spinner-border-sm"></span>
          <span>Opening report...</span>
        </div>

        <div
          v-else-if="generated_report_preview_error"
          class="dynamic-report-empty"
        >
          {{ generated_report_preview_error }}
        </div>

        <div
          v-else-if="generated_report_preview_type === 'pdf' && generated_report_preview_url"
          class="generated-report-preview-pdf-wrap"
        >
          <iframe
            class="generated-report-preview-pdf-frame"
            :src="generated_report_preview_url"
            :title="generated_report_preview_title"
          ></iframe>
        </div>

        <div
          v-else-if="generated_report_preview_type === 'excel' && generated_report_preview_rows.length"
          class="table-responsive dynamic-report-table-wrap generated-report-preview-table-wrap"
        >
          <table class="table table-sm table-hover dynamic-report-table">
            <thead>
              <tr>
                <th
                  v-for="column in generated_report_preview_columns"
                  :key="`generated-preview-head-${column.key}`"
                >
                  {{ column.label }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(row, row_index) in generated_report_preview_rows"
                :key="`generated-preview-row-${row_index}`"
              >
                <td
                  v-for="column in generated_report_preview_columns"
                  :key="`generated-preview-cell-${row_index}-${column.key}`"
                >
                  <span :title="row_value(row, column.key)">{{ row_value(row, column.key) }}</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div
          v-else
          class="dynamic-report-empty"
        >
          No preview is available for this generated report.
        </div>
      </div>
    </section>
  </div>
</div>
